import { Bot, Check, Copy, Cpu, ShieldCheck, User, X } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { RelativeTime } from './RelativeTime.tsx';
import { StatusPill } from './StatusPill.tsx';
import { Tooltip } from './Tooltip.tsx';

export type AuditActorKind = 'person' | 'agent' | 'system';

export interface AuditEntryProps {
  /** Who: a person, an AI agent, or Orgus itself. */
  actor: { name: string; kind: AuditActorKind };
  /** What, as a past-tense phrase: "stopped session". */
  action: string;
  /** The thing acted on: "Fix login race". */
  target?: string;
  /** Where: machine, project. */
  where?: string[];
  /** When. */
  at: number;
  result?: 'ok' | 'denied' | 'failed';
  /** The reason or error, one line. */
  message?: string;
  /** Signed hash of the entry. Shown short, copyable; full on hover. */
  hash?: string;
  /** Hash verified against the chain. */
  verified?: boolean;
  /** Extra detail below, such as parameters. */
  children?: ReactNode;
  className?: string;
}

const ACTOR_ICON = { person: User, agent: Bot, system: Cpu };
const ACTOR_LABEL = { person: 'Person', agent: 'AI agent', system: 'Orgus' };

function HashChip({ hash, verified }: { hash: string; verified?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard refused (insecure context): the full hash is still in the tooltip
    }
  };
  return (
    <Tooltip content={<span className="font-mono break-all">{hash}</span>}>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? `${hash.slice(0, 10)} copied` : `Copy signed hash ${hash.slice(0, 10)}`}
        className="inline-flex h-5 items-center gap-1 rounded-control border border-border-subtle px-1.5 font-mono text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:border-border hover:text-fg-muted"
      >
        {verified ? <ShieldCheck className="size-3 text-status-success" aria-hidden /> : null}
        {hash.slice(0, 10)}
        {copied ? <Check className="size-3" aria-hidden /> : <Copy className="size-3" aria-hidden />}
      </button>
    </Tooltip>
  );
}

/** One audit record: who did what, where, when, the outcome, and its signed hash. */
export function AuditEntry({ actor, action, target, where, at, result = 'ok', message, hash, verified, children, className }: AuditEntryProps) {
  const ActorIcon = ACTOR_ICON[actor.kind];
  return (
    <li className={cn('group/audit flex min-w-0 gap-3 px-1', className)}>
      <div className="flex shrink-0 flex-col items-center pt-3">
        <span
          className="flex size-7 items-center justify-center rounded-pill border border-border bg-raised text-fg-muted"
          role="img"
          aria-label={ACTOR_LABEL[actor.kind]}
        >
          <ActorIcon className="size-3.5" aria-hidden />
        </span>
        <span aria-hidden className="mt-1 w-px flex-1 bg-(--audit-rail) group-last/audit:invisible" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 py-3">
        <p className="min-w-0 text-ui text-fg-muted">
          <span className="font-medium text-fg">{actor.name}</span> {action}
          {target && (
            <>
              {' '}
              <span className="font-medium text-fg">{target}</span>
            </>
          )}
        </p>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-caption text-fg-subtle">
          <RelativeTime ts={at} />
          {where?.map((w) => (
            <span key={w} className="flex items-center gap-2">
              <span aria-hidden>·</span>
              <span className="truncate">{w}</span>
            </span>
          ))}
          {result !== 'ok' && (
            <StatusPill status="failed" label={result === 'denied' ? 'Denied' : 'Failed'} variant="plain" />
          )}
          {result === 'ok' && (
            <span className="inline-flex items-center gap-1 text-fg-subtle">
              <Check className="size-3" aria-hidden />
              Succeeded
            </span>
          )}
          {hash && <HashChip hash={hash} verified={verified} />}
        </div>
        {message && (
          <p className={cn('text-caption', result === 'ok' ? 'text-fg-muted' : 'text-status-danger')}>
            {result !== 'ok' && <X className="mr-1 inline size-3 align-[-2px]" aria-hidden />}
            {message}
          </p>
        )}
        {children}
      </div>
    </li>
  );
}

/** The list that holds AuditEntry items, with the connecting rail. */
export function AuditList({ children, className, label = 'Audit log' }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <ol aria-label={label} className={cn('flex min-w-0 flex-col', className)}>
      {children}
    </ol>
  );
}
